import { map, catchError, switchMap, withLatestFrom, debounceTime, filter } from 'rxjs/operators';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Injectable, inject } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';

import { DunningActionsSpecsService, DunningActionsService } from '../../../dunnings-module/services';
import { DunningTimelineStore } from '../../../dunnings-module/stores/dunning-timeline/dunning-timeline.store';
import { SharedTemplatesStore } from '../../../shared-module/stores/shared-templates.store';
import { TemplatesService, WebSocketService } from '../../../shared-module/services';
import { SuperDebtorDetailService } from '../../../debtors-module/services';
import { UserClearanceLevel } from '../../../authentication-module/enums';
import { UserService } from '../../../user-module/services';
import * as AppStoreActions from '../../app-store/actions';
import { WsEventType } from '../../../shared-module/enums';
import * as featureSelectors from '../selectors';
import * as featureActions from '../actions';

@Injectable({
	providedIn: 'root'
})
export class DunningActionDetailStoreGlobalEffects {

	private templatesService = inject(TemplatesService);
	private usersService = inject(UserService);
	private tlStore = inject(DunningTimelineStore);
	private templatesStore = inject(SharedTemplatesStore);
	private dunningActionsService = inject(DunningActionsService);
	private dasService = inject(DunningActionsSpecsService);
	private sdService = inject(SuperDebtorDetailService);
	private actions$ = inject(Actions);
	private store = inject(Store);
	private ws = inject(WebSocketService);

	onFormOpen$ = createEffect(() => this.actions$.pipe(
		ofType(
			featureActions.OpenForm,
			featureActions.ReOpenForm,
			featureActions.OpenDuplicationForm,
			featureActions.InitCompletionFormRequest
		),
		switchMap(_ => this.dasService.getSpecsOnce()),
		map(specs => featureActions.LoadActionSpecsSuccess({ specs }))
	));

	onPreload$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PreloadForm),
		switchMap(_ => [
			AppStoreActions.Execute(() => this.templatesStore.loadAllTemplates()),
			featureActions.LoadAllUsersRequest(),
			featureActions.LoadAssignableUsersRequest(),
			featureActions.LoadSuperAdminUsersRequest(),
			AppStoreActions.Execute(() => this.templatesStore.loadClips()),
		])
	));

	loadActionToDuplicateRequest$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadDunningActionToDuplicateRequest),
		switchMap(action => this.dunningActionsService.getActionForm(action.id).pipe(
			switchMap(model => this.dasService.getSpecsStream().pipe(
				switchMap(specs => specs[model.type].mapToDuplicatedAction(model)),
				map(entity => featureActions.LoadDunningActionSuccess({
					entity, correlationParams: action.correlationParams
				}))
			)),
			catchError(error => of(featureActions.LoadDunningActionFailure({error})))
		))
	));

	assignableUsersRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadAssignableUsersRequest),
		switchMap(action =>
			this.usersService.getUsersByClearanceLevel(UserClearanceLevel.SellerLinking).pipe(
				map(list => featureActions.LoadAssignableUsersSuccess({entity: list, correlationParams: action.correlationParams})), // LoadAssignableUsersSuccess should be aggregatable
				catchError(error =>
					of(featureActions.LoadAssignableUsersFailure({error}))
				)
			)
		)
	));

	superAdminUsersRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadSuperAdminUsersRequest),
		switchMap(action =>
			this.usersService.getUsersByClearanceLevel(UserClearanceLevel.SuperAdministration).pipe(
				map(list => featureActions.LoadSuperAdminUsersSuccess({entity: list, correlationParams: action.correlationParams})), // LoadSuperAdminUsersSuccess should be aggregatable
				catchError(error =>
					of(featureActions.LoadSuperAdminUsersFailure({error}))
				)
			)
		)
	));

	allUsersRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadAllUsersRequest),
		switchMap(action =>
			this.usersService.getAllUsers().pipe(
				map(list => featureActions.LoadAllUsersSuccess({entity: list, correlationParams: action.correlationParams})), // LoadAllUsersSuccess should be aggregatable
				catchError(error =>
					of(featureActions.LoadAllUsersFailure({error}))
				)
			)
		)
	));

	loadContactsRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadContactsRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectSuperDebtorId),
			this.actions$.pipe(ofType(featureActions.LoadDunningActionSuccess))
		),
		filter(([, superDebtorId]) => !!superDebtorId),
		switchMap(([action, superDebtorId, da]) => {
			return this.sdService.searchDunningActionContacts(superDebtorId, da.entity.isPayingCenter).pipe(
				map(entity => featureActions.LoadContactsSuccess({entity, correlationParams: action.correlationParams})), // LoadContactsSuccess should be aggregatable
				catchError(error =>
					of(featureActions.LoadContactsFailure({error}))
				)
			);
		})
	));

	loadSelectedContactsRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadSelectedContactsRequest),
		withLatestFrom(this.store.select(featureSelectors.selectDunningAction)),
		filter(([, dunningAction]) => !!dunningAction?.id),
		switchMap(([action, dunningAction]) => {
			return this.dunningActionsService.getActionSelectedContacts(dunningAction.id).pipe(
				map(list => featureActions.LoadSelectedContactsSuccess({entity: list, correlationParams: action.correlationParams})), // LoadContactsSuccess should be aggregatable
				catchError(error =>
					of(featureActions.LoadSelectedContactsFailure({error}))
				)
			);
		})
	));

	loadCommentsRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadCommentsRequest),
		switchMap(action =>
			this.dunningActionsService.getActionComments(action.id).pipe(
				map(list => featureActions.LoadCommentsSuccess({entity: list, correlationParams: action.correlationParams})), // LoadCommentsSuccess should be aggregatable
				catchError(error =>
					of(featureActions.LoadCommentsFailure({error}))
				)
			)
		)
	));

	addCommentRequest$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.AddCommentRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectDunningAction),
			this.store.select(featureSelectors.selectRawComments)),
		switchMap(([action, dunningAction, comments]) =>
			this.dunningActionsService.addComment(dunningAction.id, action.param).pipe(
				switchMap(() => [
					featureActions.AddCommentSuccess(),
					featureActions.LoadCommentsRequest(dunningAction.id),
					AppStoreActions.Execute(() => this.tlStore.updateActionProps({
						dunningActionId: dunningAction.id,
						updater: _ => ({
							id: dunningAction.id,
							commentCount: comments.length + 1
						})
					})),
				]),
				catchError(error => of(featureActions.AddCommentFailure({param: error})))
			)
		)
	));

	updateCommentRequest$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateCommentRequest),
		withLatestFrom(this.store.select(featureSelectors.selectDunningAction)),
		switchMap(([action, dunningAction]) =>
			this.dunningActionsService.updateComment(action.param).pipe(
				switchMap(() => [
					featureActions.UpdateCommentSuccess(),
					dunningAction ? featureActions.LoadCommentsRequest(dunningAction.id) : null,
				].filter(Boolean)),
				catchError(error => of(featureActions.UpdateCommentFailure({param: error})))
			)
		)
	));

	deleteCommentRequest$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.DeleteCommentRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectDunningAction),
			this.store.select(featureSelectors.selectRawComments)),
		switchMap(([action, dunningAction, comments]) =>
			this.dunningActionsService.deleteComment(action.param).pipe(
				switchMap(() => [
					featureActions.DeleteCommentSuccess(),
					!dunningAction ? null : featureActions.LoadCommentsRequest(dunningAction.id),
					!dunningAction ? null : AppStoreActions.Execute(() => this.tlStore.updateActionProps({
						dunningActionId: dunningAction.id,
						updater: _ => ({
							id: dunningAction.id,
							commentCount: comments.length - 1
						})
					})),
				].filter(Boolean)),
				catchError(error => of(featureActions.DeleteCommentFailure({param: error})))
			)
		)
	));

	loadTemplateRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadTemplateRequest),
		switchMap(action => this.templatesService.getTemplate(action.param.id).pipe(
			map(template => featureActions.LoadTemplateSuccess({entity: template})),
			catchError(error => of(featureActions.LoadTemplateFailure({error})))
		))
	));

	loadClipsRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadClipsRequest),
		switchMap(action => this.templatesService.getClips().pipe(
			map(list => featureActions.LoadClipsSuccess({entity: list, correlationParams: action.correlationParams})),
			catchError(error => of(featureActions.LoadClipsFailure({error})))
		))
	));

	loadAssignablesOperatorsRequest$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadAssignablesOperatorsRequest),
		switchMap((action) => this.dunningActionsService.getAssignablesOperators(action.param).pipe(
			map(content => featureActions.LoadAssignablesOperatorsSuccess({entity: content})),
			catchError(error => of(featureActions.LoadAssignablesOperatorsFailure(error)))
		))
	));

	loadNotifiablesUsersRequest$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadNotifiablesUsersRequest),
		switchMap((action) => this.dunningActionsService.getNotifiablesUsers(action.param).pipe(
			map(content => featureActions.LoadNotifiablesUsersSuccess({entity: content})),
			catchError(error => of(featureActions.LoadNotifiablesUsersFailure(error)))
		))
	));

	refreshUserEffect = createEffect(() => this.ws.ofType(WsEventType.RefreshAccountingDocumentsDunningLevel).pipe(
		debounceTime(500),
		withLatestFrom(
			this.store.select(featureSelectors.selectAccountingList),
			this.store.select(featureSelectors.selectDunningAction)),
		map(([action, accountingDocs, dunningAction]) => {
			const modifiedDunningActions = JSON.parse(action).AccountingDocumentIds;
			const ids = accountingDocs.items.map(item => item.accountingDocumentId)
			const isPresent = modifiedDunningActions.some(id => ids.includes(id));
			const level = dunningAction.dunningLevel;
			return isPresent ? featureActions.LoadAccountingRequest(level) : AppStoreActions.Noop()
		}),
		catchError(error => of(featureActions.LoadAccountingFailure({error})))
	));

	loadEntityCanBeCompletedRequest$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadEntityCanBeCompletedRequest),
		debounceTime(100),
		switchMap(action => this.dunningActionsService.getCanBeCompleted(action).pipe(
			map(entity => featureActions.LoadCanBeCompletedSuccess({ entity })),
			catchError(error => of(featureActions.LoadCanBeCompletedFailure({error})))
		))
	));
}
